// services/storage/api.js
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { sliceNames } from "../util/constants";
import { initializeJSONForCurrentSession, syncReduxWithS3 } from "../storage/thunks";
import { initializeAdapter, selectFormInfoForS3Url, selectQuestionIDs, selectS3PathFromType, setReferenceKey } from "../storage/slices";
import { getSessionsMetadata, getSystemMessageFn, updateSystemMessageFn } from "./queryFn";

export const getQuestionIdentifiers = async (
  params = {},
  {
    getState,
    dispatch
  }
) => {
  const { formId, formType, s3Path } = params;
  const mapIds = (questions = []) => questions.map((q) => q.id).filter(Boolean);
  let pathToUse

  // When viewing an existing form with a known s3Path, load questions
  // directly without requiring an active reporting period.
  if (formId && s3Path) {
    pathToUse = s3Path;
    const initializationParms = { formId, s3Path };
    dispatch(initializeAdapter(initializationParms));
    try {
      const response = await dispatch(
        syncReduxWithS3({ s3Path, formId, formType })
      ).unwrap();
      const ids = response?.questions?.length ? mapIds(response.questions) : [];
      if (ids?.length) {
        return { data: ids };
      }
    } catch (syncError) {
      console.warn("syncReduxWithS3 failed for direct s3Path load:", syncError);
    }

    const qIds = selectQuestionIDs(getState(), { type: formType, identifier: formId, path: pathToUse });
    if (qIds?.length) {
      return { data: qIds };
    }
    const responseFromSelector = selectFormInfoForS3Url(getState(), {
      formType, formId, s3Url: pathToUse
    });
    const fallbackQuestions = responseFromSelector?.data?.questions;
    const fallbackIdentifiers = Array.isArray(fallbackQuestions) ? fallbackQuestions.map(q => q.id).filter(Boolean) : [];
    return { data: fallbackIdentifiers };
  }

  // Normal path: initialize from active reporting period
  const activePeriod = await dispatch(
    initializeJSONForCurrentSession()
  ).unwrap();

  if (formId) {
    const initializationParms = {
      formId,
      s3Path: s3Path || selectS3PathFromType(getState(), formType),
    };
    const validatedS3Path = initializationParms?.s3Path;
    const alreadyStored = activePeriod?.paths?.find(
      ({ s3Path: _s3Path }) => s3Path && (_s3Path === s3Path)
    );
    if (alreadyStored?.s3Path) {
      pathToUse = alreadyStored?.s3Path
      dispatch(
        setReferenceKey({
          key: formId,
          s3Path: alreadyStored?.s3Path,
        })
      );
      const overrideIds = selectQuestionIDs(getState(), { path: alreadyStored?.s3Path });
      if(overrideIds?.length) {
        return overrideIds
      }
    } else if (validatedS3Path && formType) {
      pathToUse = validatedS3Path
      dispatch(initializeAdapter(initializationParms));
      const overridedResponse = await dispatch(
        syncReduxWithS3({
          s3Path: validatedS3Path,
          formId,
          formType,
        })
      ).unwrap();
      let ids = overridedResponse?.questions?.length
        ? mapIds(overridedResponse.questions)
        : [];
      if (ids?.length) {
        return {
          data: ids,
        };
      }
    }
  }
  
  const qIds = selectQuestionIDs(getState(), { type: formType, identifier: formId, path: pathToUse });
  if(qIds?.length) {
    return {
      data: qIds
    }
  }
  const responseFromSelector = selectFormInfoForS3Url(getState(), {
    formType: formType,
    formId,
    s3Url: pathToUse
  })
  const fallbackQuestions = responseFromSelector?.data?.questions
  const fallbackIdentifiers = Array.isArray(fallbackQuestions) ? fallbackQuestions.map(q => q.id).filter(Boolean) : []
  
  return {
    data: fallbackIdentifiers
  }
};

const systemMessageTag = 'SystemMessage'

export const shared = createApi({
  reducerPath: sliceNames.common,
  baseQuery: fakeBaseQuery(),
  tagTypes: ["SharedServices"],
  endpoints: (builder) => ({
    questionIdentifiers: builder.query({
      queryFn: getQuestionIdentifiers,
    }),
    getSystemMessage: builder.query({
      queryFn:getSystemMessageFn,
      providesTags: [systemMessageTag]
    }),
    getSessionsMetadata: builder.query({
      queryFn:getSessionsMetadata,
    }),
    updateSystemMessage: builder.mutation({
      queryFn: updateSystemMessageFn,
      invalidatesTags: [systemMessageTag]
    }),
  }),
});


export const { useQuestionIdentifiersQuery, useUpdateSystemMessageMutation, useGetSystemMessageQuery, useGetSessionsMetadataQuery } = shared;
